import { useState } from 'react';
import { runDualAUS, generateAUSInsights, saveAUSSelection, AUS_SYSTEMS, REP_WARRANTY_STATUS } from '../../services/ausService';

const DualAUSComponent = ({ loan, onAUSComplete }) => {
  const [ausResults, setAusResults] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAUS, setSelectedAUS] = useState(null);
  const [ausHistory, setAusHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const handleRunAUS = async () => {
    setLoading(true);
    
    try {
      const results = await runDualAUS(loan);
      setAusResults(results);
      
      // Generate AI insights
      const insights = generateAUSInsights(results.du, results.lpa, loan);
      setAiInsights(insights);
      
      // Add to history
      setAusHistory(prev => [...prev, {
        timestamp: results.timestamp,
        du: results.du,
        lpa: results.lpa,
        runNumber: prev.length + 1
      }]);
    } catch (error) {
      console.error('AUS execution error:', error);
      alert('Failed to run AUS. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAUS = async (system) => {
    if (!ausResults) return;
    
    const selection = await saveAUSSelection(loan.id, system, ausResults);
    setSelectedAUS(system);
    
    if (onAUSComplete) {
      onAUSComplete(system, ausResults);
    }
    
    alert(`${system} selected as governing AUS findings. This selection has been logged.`);
  };

  const getRepWarrantyIcon = (status) => {
    if (status === REP_WARRANTY_STATUS.GRANTED) {
      return <i className="ti ti-circle-check text-success" style={{ fontSize: '1.5rem' }}></i>;
    } else if (status === REP_WARRANTY_STATUS.PARTIAL) {
      return <i className="ti ti-alert-circle text-warning" style={{ fontSize: '1.5rem' }}></i>;
    } else {
      return <i className="ti ti-circle-x text-danger" style={{ fontSize: '1.5rem' }}></i>;
    }
  };

  const getRepWarrantyBadge = (status) => {
    if (status === REP_WARRANTY_STATUS.GRANTED) {
      return <span className="badge bg-success">Granted</span>;
    } else if (status === REP_WARRANTY_STATUS.PARTIAL) {
      return <span className="badge bg-warning">Partial</span>;
    } else {
      return <span className="badge bg-danger">Denied</span>;
    }
  };

  const getRecommendationBadge = (code) => {
    if (code === 'approve' || code === 'accept') {
      return <span className="badge bg-success-lt" style={{ fontSize: '1.1rem', padding: '0.5rem 1rem' }}>✓ Approved</span>;
    } else if (code === 'refer' || code === 'caution') {
      return <span className="badge bg-warning-lt" style={{ fontSize: '1.1rem', padding: '0.5rem 1rem' }}>⚠ Refer</span>;
    } else {
      return <span className="badge bg-danger-lt" style={{ fontSize: '1.1rem', padding: '0.5rem 1rem' }}>✗ Ineligible</span>;
    }
  };

  if (!ausResults && !loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="mb-3">
            <i className="ti ti-robot" style={{ fontSize: '4rem', color: 'var(--tblr-primary)' }}></i>
          </div>
          <h3 className="card-title mb-3">Dual AUS - Underwriting Intelligence</h3>
          <p className="text-secondary mb-4">
            Run Desktop Underwriter (DU) and Loan Product Advisor (LPA) simultaneously to compare findings and Rep & Warranty relief.
          </p>
          <button className="btn btn-primary btn-lg" onClick={handleRunAUS}>
            <i className="ti ti-play me-2"></i>
            Run AUS (DU + LPA)
          </button>
          
          {ausHistory.length > 0 && (
            <div className="mt-4">
              <button className="btn btn-ghost-secondary" onClick={() => setShowHistory(!showHistory)}>
                <i className="ti ti-history me-1"></i>
                View AUS History ({ausHistory.length} runs)
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status"></div>
          <h3 className="mb-2">Running Dual AUS...</h3>
          <p className="text-secondary">Executing Desktop Underwriter and Loan Product Advisor in parallel</p>
          <div className="progress mt-3" style={{ height: '8px' }}>
            <div className="progress-bar progress-bar-indeterminate"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Actions */}
      <div className="card mb-3">
        <div className="card-header">
          <h3 className="card-title">
            <i className="ti ti-robot me-2"></i>
            Dual AUS Results
          </h3>
          <div className="card-actions">
            <button className="btn btn-sm btn-primary me-2" onClick={handleRunAUS}>
              <i className="ti ti-refresh me-1"></i>
              Re-run AUS
            </button>
            <button className="btn btn-sm btn-ghost-secondary" onClick={() => setShowHistory(!showHistory)}>
              <i className="ti ti-history me-1"></i>
              History ({ausHistory.length})
            </button>
          </div>
        </div>
      </div>

      {/* AUS History Timeline */}
      {showHistory && ausHistory.length > 0 && (
        <div className="card mb-3">
          <div className="card-header">
            <h3 className="card-title">AUS History Timeline</h3>
          </div>
          <div className="card-body">
            <div className="list-group list-group-flush">
              {ausHistory.map((run, idx) => (
                <div key={idx} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <span className="badge bg-blue-lt">Run #{run.runNumber}</span>
                    </div>
                    <div className="col">
                      <div className="text-secondary small">
                        {new Date(run.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="col-auto">
                      <span className="me-2">DU: {run.du.recommendation}</span>
                      <span>LPA: {run.lpa.recommendation}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Insights Panel */}
      {aiInsights && (
        <div className="card mb-3">
          <div className="card-header bg-purple-lt">
            <h3 className="card-title">
              <i className="ti ti-bulb me-2"></i>
              AI AUS Insights
            </h3>
          </div>
          <div className="card-body">
            <div className="alert alert-info mb-3">
              <strong>Summary:</strong> {aiInsights.summary}
            </div>

            {aiInsights.differences.length > 0 && (
              <div className="mb-3">
                <h4 className="mb-2">Key Differences</h4>
                {aiInsights.differences.map((diff, idx) => (
                  <div key={idx} className="alert alert-warning">
                    <strong>{diff.category}:</strong> DU = {diff.du}, LPA = {diff.lpa}
                    <div className="text-secondary small mt-1">{diff.explanation}</div>
                  </div>
                ))}
              </div>
            )}

            {aiInsights.repWarrantyAnalysis.length > 0 && (
              <div className="mb-3">
                <h4 className="mb-2">Rep & Warranty Analysis</h4>
                {aiInsights.repWarrantyAnalysis.map((analysis, idx) => (
                  <div key={idx} className="alert alert-primary">
                    <strong>{analysis.category}:</strong> DU {analysis.du} vs LPA {analysis.lpa}
                    <div className="text-secondary small mt-1">{analysis.explanation}</div>
                    <div className="text-secondary small mt-1">
                      <i className="ti ti-info-circle me-1"></i>
                      {analysis.reason}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {aiInsights.recommendations.length > 0 && (
              <div>
                <h4 className="mb-2">Recommendations</h4>
                {aiInsights.recommendations.map((rec, idx) => (
                  <div key={idx} className="alert alert-success">
                    <div className="d-flex">
                      <div><i className="ti ti-arrow-up-right me-2"></i></div>
                      <div>
                        <strong>{rec.title}</strong>
                        <div className="text-secondary small">{rec.description}</div>
                        <div className="mt-1">
                          <span className="badge bg-green-lt">Impact: {rec.impact}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Side-by-Side DU and LPA Results */}
      <div className="row row-cards mb-3">
        {/* Desktop Underwriter (DU) */}
        <div className="col-lg-6">
          <div className={`card ${selectedAUS === AUS_SYSTEMS.DU ? 'border-primary' : ''}`}>
            <div className="card-header bg-blue-lt">
              <h3 className="card-title">
                <i className="ti ti-building-bank me-2"></i>
                Desktop Underwriter (DU)
              </h3>
              {selectedAUS === AUS_SYSTEMS.DU && (
                <div className="card-actions">
                  <span className="badge bg-primary">Selected AUS</span>
                </div>
              )}
            </div>
            <div className="card-body">
              <div className="mb-3 text-center">
                <div className="mb-2">
                  {getRecommendationBadge(ausResults.du.recommendationCode)}
                </div>
                <div className="text-secondary small">Casefile: {ausResults.du.casefileId}</div>
              </div>

              <div className="mb-3">
                <h4 className="mb-2">Key Messages</h4>
                <ul className="mb-0">
                  {ausResults.du.messages.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-3">
                <h4 className="mb-2">Findings Summary</h4>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td>Credit Score</td>
                      <td className="text-end fw-bold">{ausResults.du.findings.creditScore}</td>
                    </tr>
                    <tr>
                      <td>LTV</td>
                      <td className="text-end fw-bold">{ausResults.du.findings.ltv}%</td>
                    </tr>
                    <tr>
                      <td>DTI</td>
                      <td className="text-end fw-bold">{ausResults.du.findings.dti}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!selectedAUS && (
                <button
                  className="btn btn-primary w-100"
                  onClick={() => handleSelectAUS(AUS_SYSTEMS.DU)}
                >
                  <i className="ti ti-check me-1"></i>
                  Proceed with DU
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Loan Product Advisor (LPA) */}
        <div className="col-lg-6">
          <div className={`card ${selectedAUS === AUS_SYSTEMS.LPA ? 'border-primary' : ''}`}>
            <div className="card-header bg-green-lt">
              <h3 className="card-title">
                <i className="ti ti-building-community me-2"></i>
                Loan Product Advisor (LPA)
              </h3>
              {selectedAUS === AUS_SYSTEMS.LPA && (
                <div className="card-actions">
                  <span className="badge bg-primary">Selected AUS</span>
                </div>
              )}
            </div>
            <div className="card-body">
              <div className="mb-3 text-center">
                <div className="mb-2">
                  {getRecommendationBadge(ausResults.lpa.recommendationCode)}
                </div>
                <div className="text-secondary small">Casefile: {ausResults.lpa.casefileId}</div>
              </div>

              <div className="mb-3">
                <h4 className="mb-2">Key Messages</h4>
                <ul className="mb-0">
                  {ausResults.lpa.messages.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-3">
                <h4 className="mb-2">Findings Summary</h4>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td>Credit Score</td>
                      <td className="text-end fw-bold">{ausResults.lpa.findings.creditScore}</td>
                    </tr>
                    <tr>
                      <td>LTV</td>
                      <td className="text-end fw-bold">{ausResults.lpa.findings.ltv}%</td>
                    </tr>
                    <tr>
                      <td>DTI</td>
                      <td className="text-end fw-bold">{ausResults.lpa.findings.dti}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!selectedAUS && (
                <button
                  className="btn btn-success w-100"
                  onClick={() => handleSelectAUS(AUS_SYSTEMS.LPA)}
                >
                  <i className="ti ti-check me-1"></i>
                  Proceed with LPA
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rep & Warranty Comparison Matrix */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="ti ti-shield-check me-2"></i>
            Rep & Warranty Relief Comparison
          </h3>
        </div>
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="text-center" style={{ width: '200px' }}>
                  <div className="d-flex align-items-center justify-content-center">
                    <i className="ti ti-building-bank me-2"></i>
                    DU
                  </div>
                </th>
                <th className="text-center" style={{ width: '200px' }}>
                  <div className="d-flex align-items-center justify-content-center">
                    <i className="ti ti-building-community me-2"></i>
                    LPA
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="fw-bold">Income</div>
                  <div className="text-secondary small">Income verification relief</div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.du.repWarranty.income.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.du.repWarranty.income.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.du.repWarranty.income.explanation}
                  </div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.lpa.repWarranty.income.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.lpa.repWarranty.income.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.lpa.repWarranty.income.explanation}
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="fw-bold">Assets</div>
                  <div className="text-secondary small">Asset verification relief</div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.du.repWarranty.assets.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.du.repWarranty.assets.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.du.repWarranty.assets.explanation}
                  </div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.lpa.repWarranty.assets.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.lpa.repWarranty.assets.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.lpa.repWarranty.assets.explanation}
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="fw-bold">Employment</div>
                  <div className="text-secondary small">Employment verification relief</div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.du.repWarranty.employment.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.du.repWarranty.employment.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.du.repWarranty.employment.explanation}
                  </div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.lpa.repWarranty.employment.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.lpa.repWarranty.employment.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.lpa.repWarranty.employment.explanation}
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="fw-bold">Appraisal</div>
                  <div className="text-secondary small">Appraisal waiver eligibility</div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.du.repWarranty.appraisal.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.du.repWarranty.appraisal.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.du.repWarranty.appraisal.explanation}
                  </div>
                </td>
                <td className="text-center">
                  <div className="mb-1">{getRepWarrantyIcon(ausResults.lpa.repWarranty.appraisal.status)}</div>
                  <div>{getRepWarrantyBadge(ausResults.lpa.repWarranty.appraisal.status)}</div>
                  <div className="text-secondary small mt-2" style={{ fontSize: '0.75rem' }}>
                    {ausResults.lpa.repWarranty.appraisal.explanation}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <div className="row">
            <div className="col">
              <div className="d-flex align-items-center">
                <i className="ti ti-circle-check text-success me-2"></i>
                <span className="small">Granted = Full relief</span>
              </div>
            </div>
            <div className="col">
              <div className="d-flex align-items-center">
                <i className="ti ti-alert-circle text-warning me-2"></i>
                <span className="small">Partial = Conditional relief</span>
              </div>
            </div>
            <div className="col">
              <div className="d-flex align-items-center">
                <i className="ti ti-circle-x text-danger me-2"></i>
                <span className="small">Denied = No relief</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualAUSComponent;
